import { assertNever } from "@p0security/frontend-core";
import { ClipDiv } from "@p0security/frontend-core/react";
import { Node, isNode } from "@p0security/shared/graph/types";
import { AssessmentNodes } from "@p0security/shared/types/assessment/data/base";
import { Space, Typography } from "@p0security/ui";
import { ScopeContext } from "components/Assessment/contexts/ScopeContext";
import { GraphTooltip } from "components/GraphTooltip";
import { useContext, useMemo } from "react";

import {
  awsResourceInfo,
  azureAdResourceInfo,
  azureResourceInfo,
  customResourceInfo,
  gcpResourceInfo,
  k8sResourceInfo,
  oktaResourceInfo,
  resourceLabels,
  workspaceResourceInfo,
} from "../node/resource/labels";
import { HasAddTerm, ShowHideTerm } from "./ShowHide";

export const Resource: React.FC<
  Partial<HasAddTerm> & {
    resource:
      | Node<AssessmentNodes, "grant">
      | Node<AssessmentNodes, "resource">;
    shortPath?: boolean;
    index?: number;
  }
> = (props) => {
  const { resource, shortPath, terms, onAddTerm, index } = props;
  const { provider } = useContext(ScopeContext);

  const info = useMemo(() => {
    if (isNode("grant")(resource)) {
      if (index === undefined) {
        throw new Error("Index must be provided for 'grant' resources.");
      }
      switch (resource.data.provider) {
        case "aws":
          return awsResourceInfo(resource, index, shortPath);
        case "azure":
          return azureResourceInfo(resource.data.resources[index]);
        case "azure-ad":
        case "entra-id":
          return azureAdResourceInfo(resource.data.resources[index]);
        case "gcp":
          return gcpResourceInfo(resource.data.resources[index], shortPath);
        case "k8s":
          return k8sResourceInfo(resource.data.resources[index], shortPath);
        case "workspace":
          return workspaceResourceInfo(
            resource.data.resources[index],
            shortPath
          );
        case "custom":
          return customResourceInfo(resource.data.resources[index], shortPath);
        case "okta":
          return oktaResourceInfo(resource.data.resources[index]);
        default:
          return assertNever(resource.data.provider);
      }
    } else if (isNode("resource")(resource)) {
      return resourceLabels(resource.data, provider, shortPath);
    } else {
      return assertNever(resource);
    }
  }, [provider, resource, shortPath, index]);

  const icon = useMemo(
    () =>
      info?.url && (
        <img src={info.url} width={14} height={14} className="inline" />
      ),
    [info?.url]
  );
  return info ? (
    <GraphTooltip
      title={
        <Space direction="vertical">
          <div>
            {icon}&nbsp;{info.service}
            {info.type ? `\xa0${info.type}` : ""}
          </div>
          <div>{info.isService ? "Entire service" : info.id}</div>
          <div>
            <Typography.Text code>{info.locator}</Typography.Text>
          </div>
          {onAddTerm && (
            <ShowHideTerm
              term={`${info.isGeneric ? "grant=resource:" : "resource="}"${
                info.locator
              }"`}
              name="resources"
              onAddTerm={onAddTerm}
              terms={terms}
            />
          )}
        </Space>
      }
      width="400px"
    >
      <ClipDiv className="max-w-[13.5em] inline-block">
        {icon}&nbsp;{info.id}
      </ClipDiv>
    </GraphTooltip>
  ) : null;
};
